import React, { useEffect, useMemo, useState } from "react";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { BiPackage, BiPurchaseTag } from "react-icons/bi";
import { TbStack2 } from "react-icons/tb";
import backendVerifiedUser from "../../api/backendVerifiedUser";
import DashboardTiles from "../Dashboard/DashboardTiles";
import ReportsDateFilter from "./ReportsDateFilter";
import { computeDateRange } from "./reportFilters";

export default function ItemReportTab() {
  const today = useMemo(() => new Date(), []);
  const [rangeMode, setRangeMode] = useState("month");
  const [singleDate, setSingleDate] = useState(today);
  const [dateRange, setDateRange] = useState([
    new Date(today.getFullYear(), today.getMonth(), 1),
    today,
  ]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const { startDate, endDate } = useMemo(
    () => computeDateRange(rangeMode, today, singleDate, dateRange),
    [rangeMode, singleDate, dateRange, today]
  );

  useEffect(() => {
    let cancelled = false;
    const fetchReport = async () => {
      setLoading(true);
      try {
        const params = {};
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;

        const response = await backendVerifiedUser.get("/reports/items", { params });
        if (!cancelled) setReport(response.data);
      } catch (err) {
        console.log(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchReport();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  const summary = report?.summary || {};
  const items = report?.items || [];

  const kpiData = [
    { amount: summary.totalProducts || 0, title: "Products Sold", icon: <BiPackage /> },
    { amount: summary.totalUnitsSold || 0, title: "Total Units Sold", icon: <TbStack2 /> },
    { amount: summary.totalSales || 0, title: "Total Sales", icon: <BiPurchaseTag />, prefix: "RS" },
  ];

  const handleExport = () => {
    if (!report) return;

    const itemRows = [
      ["Product", "Article #", "Units Sold", "Orders", "Total Sales", "Total Discount"],
      ...items.map((i) => [i.title, i.article_name, i.totalQuantity, i.orderCount, i.totalSales, i.totalDiscount]),
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(itemRows), "Item Report");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, `item-report-${startDate || "all"}-to-${endDate || "all"}.xlsx`);
  };

  return (
    <div>
      <div className="flex items-center justify-end mb-3">
        <Button
          label="Export to Excel"
          icon="pi pi-file-excel"
          className="p-primary-btn"
          onClick={handleExport}
          disabled={!report}
        />
      </div>

      <ReportsDateFilter
        rangeMode={rangeMode}
        setRangeMode={setRangeMode}
        singleDate={singleDate}
        setSingleDate={setSingleDate}
        dateRange={dateRange}
        setDateRange={setDateRange}
        loading={loading}
      />

      {report && (
        <>
          <div className="mb-3 font-semibold text-black/50 text-lg ml-1">Summary</div>
          <DashboardTiles data={kpiData} />

          <div className="mt-8">
            <div className="my-3 font-semibold text-black/50 text-xl ml-1">Product-wise Sales</div>
            <div className="card shadow-md">
              <DataTable value={items} stripedRows size="small" paginator rows={10} emptyMessage="No sales for the selected filters.">
                <Column field="title" header="Product" />
                <Column field="article_name" header="Article #" style={{ width: "110px" }} />
                <Column field="totalQuantity" header="Units Sold" style={{ width: "110px" }} />
                <Column field="orderCount" header="Orders" style={{ width: "90px" }} />
                <Column
                  field="totalSales"
                  header="Total Sales"
                  body={(row) => `RS ${Number(row.totalSales || 0).toLocaleString("en-IN")}`}
                />
                <Column
                  field="totalDiscount"
                  header="Total Discount"
                  body={(row) => `RS ${Number(row.totalDiscount || 0).toLocaleString("en-IN")}`}
                />
              </DataTable>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
